import type { ISocialPrompt, ISocialPromptSeriesReadWithStatus, IThoughtLeadershipEntry } from "../Types/account";
import { SOCIAL_CONTENT_TYPE, SocialPromptSeriesKind } from "../Types/account";
import { formatEnumValue } from "./formatters";

/** Effective step index within a series (1-based). */
export function getPromptStepNumber(prompt: ISocialPrompt): number | null {
    const n = prompt.step_number ?? prompt.number_in_series;
    if (n == null || Number.isNaN(Number(n))) return null;
    return Number(n);
}

export function sortPromptsByStep(prompts: ISocialPrompt[]): ISocialPrompt[] {
    return [...prompts].sort((a, b) => {
        const sa = getPromptStepNumber(a) ?? 999;
        const sb = getPromptStepNumber(b) ?? 999;
        return sa - sb;
    });
}

/** Human label: `Step 2 of 5 - Email` or fallbacks when data is partial. */
export function getPromptStepLabel(prompt: ISocialPrompt, contentTypeLabel?: string): string {
    const step = getPromptStepNumber(prompt);
    const total = prompt.total_steps ?? null;
    const ct =
        contentTypeLabel ??
        (prompt.content_type ? formatEnumValue(prompt.content_type) : "Prompt");
    if (step != null && total != null) {
        return `Step ${step} of ${total} - ${ct}`;
    }
    if (step != null) {
        return `Step ${step} - ${ct}`;
    }
    return ct;
}

/** Short badge: `Step 2 of 5` or `Step - of 5` / `Step 2 of -` when partial. */
export function getPromptStepBadgeText(prompt: ISocialPrompt): string {
    const step = getPromptStepNumber(prompt);
    const total = prompt.total_steps ?? null;
    if (step != null && total != null) return `Step ${step} of ${total}`;
    if (total != null) return `Step - of ${total}`;
    if (step != null) return `Step ${step} of -`;
    return "";
}

export function formatSeriesKind(kind: SocialPromptSeriesKind): string {
    switch (kind) {
        case SocialPromptSeriesKind.STANDALONE:
            return "Standalone";
        case SocialPromptSeriesKind.SEQUENCE:
            return "Sequence";
        case SocialPromptSeriesKind.VARIATION_SET:
            return "Variation set";
        default:
            return String(kind);
    }
}

/** Ordered channel summary e.g. `Email → Call → LinkedIn DM`. */
export function getSeriesChannelSummary(series: ISocialPromptSeriesReadWithStatus): string {
    const ordered = sortPromptsByStep(series.prompts);
    const parts = ordered
        .map((p) => p.content_type)
        .filter((t): t is SOCIAL_CONTENT_TYPE => t != null)
        .map((t) => formatEnumValue(t));
    return parts.join(" → ");
}

export function formatSeriesCreatedAt(createdAt: string | null | undefined): string | null {
    if (!createdAt?.trim()) return null;
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function getSeriesCreatedAtTime(createdAt: string | null | undefined): number {
    if (!createdAt?.trim()) return 0;
    const time = new Date(createdAt).getTime();
    return Number.isNaN(time) ? 0 : time;
}

/** Newest `created_at` first; series without a date sort to the end. */
export function sortSeriesByMostRecent(
    seriesList: ISocialPromptSeriesReadWithStatus[]
): ISocialPromptSeriesReadWithStatus[] {
    return [...seriesList].sort(
        (a, b) => getSeriesCreatedAtTime(b.created_at) - getSeriesCreatedAtTime(a.created_at)
    );
}

/** Steps and channels only (no generated date). */
export function getSeriesStepsMeta(series: ISocialPromptSeriesReadWithStatus): string {
    return [
        `${series.total_steps} step${series.total_steps !== 1 ? "s" : ""}`,
        getSeriesChannelSummary(series),
    ].join(" · ");
}

/** Short helper copy under the sequence rail panel title. */
export function getSequenceRailHint(): string {
    return "Your outreach steps in send order. Select one to compose or send.";
}

/** Attach series-level fields onto each prompt for UI and events. */
export function enrichPromptsWithSeriesContext(
    series: ISocialPromptSeriesReadWithStatus
): ISocialPrompt[] {
    return sortPromptsByStep(series.prompts).map((p) => ({
        ...p,
        series_id: series.id,
        total_steps: series.total_steps,
        series_kind: series.kind,
        contact_id: p.contact_id ?? series.contact_id ?? null,
        step_number: getPromptStepNumber(p) ?? p.step_number ?? p.number_in_series ?? null,
    }));
}

export function flattenSeriesToPrompts(seriesList: ISocialPromptSeriesReadWithStatus[]): ISocialPrompt[] {
    return seriesList.flatMap((s) => enrichPromptsWithSeriesContext(s));
}

/** TL entries default to LinkedIn post when the orchestrator omits content_type. */
export function resolveThoughtLeadershipContentType(
    entry: IThoughtLeadershipEntry,
    prompt?: ISocialPrompt | null
): SOCIAL_CONTENT_TYPE {
    if (entry.content_type) return entry.content_type;
    if (prompt?.content_type) return prompt.content_type;
    return SOCIAL_CONTENT_TYPE.LINKEDIN_POST;
}
